import { useCallback } from 'react';

import { DialogContent, DialogTitle, Box, Button, DialogActions, Typography, IconButton, SxProps, alpha, } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Close } from '@mui/icons-material';
import { closeDialog } from '../../redux/reducer/dialogSlice';
import { RootState } from '../../redux/store';
import { Theme } from '@emotion/react';
import { useLocation } from 'react-router-dom';
import { TEMPLE } from '../common/templeStyles';

type GlobalDialogContentProps = {
    dialogBody: React.ReactNode;
    dialogFooter?: React.ReactNode;
    isShowDialogFooter?: boolean;
    dialogContentStyles?: SxProps<Theme>;
};

const GlobalDialogContent = ({ dialogBody, dialogFooter, isShowDialogFooter = true, dialogContentStyles = {}, }: GlobalDialogContentProps) => {
    const dispatch = useDispatch();
    const { title } = useSelector((state: RootState) => state.dialog);
    const projectLocation = useSelector((state: RootState) => state.projectLocation.CreateProjectlocation);
    const lang = useSelector((state: RootState) => state.lang.language);

    const location = useLocation();
    const handleClose = useCallback(() => { dispatch(closeDialog()); }, [dispatch]);

    return (
        <Box
            sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                backgroundColor: TEMPLE.cream,
                backgroundImage: `linear-gradient(180deg, #fff 0%, ${TEMPLE.cream} 100%)`,
                border: `1px solid ${TEMPLE.line}`,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(135deg, ${alpha(TEMPLE.saffron, 0.06)} 0%, transparent 60%)`,
                    pointerEvents: 'none',
                    zIndex: -1,
                },
            }}
        >
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    p: 2,
                    background: `linear-gradient(90deg, ${TEMPLE.cream2} 0%, #fff 65%)`,
                    borderBottom: `1px solid ${TEMPLE.line}`,
                    transition: 'all 0.2s ease',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: TEMPLE.maroon,
                            pb: 1,
                            borderBottom: `2px solid ${TEMPLE.saffron}`,
                            letterSpacing: '-0.2px',
                        }}
                    >
                        {title || projectLocation?.address || 'Choose Location'}
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            border: `1px solid ${TEMPLE.line}`,
                            color: TEMPLE.maroon,
                            backgroundColor: '#fff',
                            '&:hover': {
                                backgroundColor: alpha(TEMPLE.saffron, 0.1),
                                borderColor: TEMPLE.saffron,
                                transform: 'rotate(90deg) scale(1.08)',
                                transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            },
                        }}
                    >
                        <Close fontSize="small" sx={{ color: TEMPLE.maroon }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ overflow: 'auto', maxHeight: "40rem", padding: "16px", paddingTop: "15px !important", backgroundColor: TEMPLE.cream, ...dialogContentStyles }}>
                {dialogBody}
            </DialogContent>

            {isShowDialogFooter && location.pathname !== '/admin/master/workflow' && (
                <DialogActions
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        p: 1.5,
                        background: `linear-gradient(90deg, ${TEMPLE.cream2} 0%, #fff 65%)`,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        borderTop: `1px solid ${TEMPLE.line}`,
                    }}
                >
                    <Button
                        variant="outlined"
                        sx={{
                            borderRadius: '12px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            color: TEMPLE.maroon,
                            borderColor: TEMPLE.line,
                            '&:hover': {
                                borderColor: TEMPLE.maroon,
                                backgroundColor: alpha(TEMPLE.maroon, 0.06),
                            },
                        }}
                        onClick={handleClose}
                    >
                        {lang === 'English' ? 'Close' : 'बंद करें'}
                    </Button>

                    {dialogFooter}
                </DialogActions>
            )}

        </Box>
    );
};

export default GlobalDialogContent;